import React from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"

interface GuideModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  content: string
}

/**
 * Modal component for displaying markdown guide content
 */
const GuideModal: React.FC<GuideModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
}) => (
  <Dialog isOpen={isOpen} handleClose={onClose} width={600}>
    <div className="bg-system-blue-800 border border-system-blue-600/50 rounded-system-xl shadow-2xl flex flex-col max-h-[80vh]">
      {/* Static Header */}
      <div className="flex items-center justify-between p-6 pb-4 border-b border-system-blue-600/30">
        <h2 className="text-xl font-bold text-white">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-white focus:outline-none transition-colors"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-6 text-sm leading-relaxed prose prose-sm max-w-none text-left">
        {/* remark-gfm autolinks the bare URLs the connector guides embed
            ("direct link: https://..."); without it they render as dead text. */}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({
              href,
              children,
            }: {
              href?: string
              children?: React.ReactNode
            }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-info underline"
              >
                {children}
              </a>
            ),
            h1: ({ children }: { children?: React.ReactNode }) => (
              <h1 className="text-xl font-extrabold text-white mb-3 mt-0">
                {children}
              </h1>
            ),
            h2: ({ children }: { children?: React.ReactNode }) => (
              <h2 className="text-lg font-bold text-white mb-2 mt-4">
                {children}
              </h2>
            ),
            h3: ({ children }: { children?: React.ReactNode }) => (
              <h3 className="text-base font-bold text-white mb-1 mt-3">
                {children}
              </h3>
            ),
            p: ({ children }: { children?: React.ReactNode }) => (
              <p className="mb-3 text-white">{children}</p>
            ),
            ul: ({ children }: { children?: React.ReactNode }) => (
              <ul className="list-disc list-inside mb-3 space-y-1 text-white">
                {children}
              </ul>
            ),
            ol: ({ children }: { children?: React.ReactNode }) => (
              <ol className="list-decimal list-inside mb-3 space-y-1 text-white">
                {children}
              </ol>
            ),
            li: ({ children }: { children?: React.ReactNode }) => (
              <li className="text-white">{children}</li>
            ),
            strong: ({ children }: { children?: React.ReactNode }) => (
              <strong className="font-bold text-brand">{children}</strong>
            ),
            em: ({ children }: { children?: React.ReactNode }) => (
              <em className="italic text-gray-300">{children}</em>
            ),
            code: ({ children }: { children?: React.ReactNode }) => (
              <code className="bg-system-blue-600/50 px-1.5 py-0.5 rounded text-xs font-mono text-white border border-system-blue-500/30">
                {children}
              </code>
            ),
            blockquote: ({ children }: { children?: React.ReactNode }) => (
              <blockquote className="border-l-4 border-brand pl-3 italic text-gray-300 my-3 bg-system-blue-700/50 py-2 rounded-r">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-4 border-system-blue-600/30" />,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      {/* Static Footer */}
      <div className="px-6 py-3 border-t border-system-blue-600/30 flex justify-end">
        <Button
          variant={{ variant: "solid", color: "blue" }}
          size="sm"
          onClick={onClose}
        >
          Got it
        </Button>
      </div>
    </div>
  </Dialog>
)

export default GuideModal
